if (!customElements.get('dawn-banner-slider')) {
  class DawnBannerSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.responsive-banner-slider__slide'));
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      this.previousButton = this.querySelector('[data-slider-previous]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.status = this.querySelector('[data-slider-status]');
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.loop = this.dataset.loop === 'true';
      this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.timer = null;

      if (this.slides.length < 2) return;

      this.previousButton?.addEventListener('click', () => this.previous(true));
      this.nextButton?.addEventListener('click', () => this.next(true));
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.show(index, true);
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.previous(true);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next(true);
        }
      });

      this.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.stopAutoplay();
      }, { passive: true });

      this.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        const distanceX = touch.clientX - this.touchStartX;
        const distanceY = touch.clientY - this.touchStartY;

        if (Math.abs(distanceX) > 45 && Math.abs(distanceX) > Math.abs(distanceY)) {
          distanceX < 0 ? this.next(true) : this.previous(true);
        } else {
          this.startAutoplay();
        }
      }, { passive: true });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
      }

      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      });

      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('.responsive-banner-slider__slide');
        if (!slide) return;
        this.stopAutoplay();
        this.show(Number(slide.dataset.slideIndex), false);
      });

      document.addEventListener('visibilitychange', this.handleVisibilityChange = () => {
        document.hidden ? this.stopAutoplay() : this.startAutoplay();
      });

      this.updateControls();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.handleVisibilityChange) {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      }
    }

    show(index, userInitiated = false) {
      if (!this.slides.length) return;

      let nextIndex = index;
      if (this.loop) {
        nextIndex = (index + this.slides.length) % this.slides.length;
      } else {
        nextIndex = Math.min(Math.max(index, 0), this.slides.length - 1);
      }

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          if (active) {
            if (element.dataset.sliderOriginalTabindex !== undefined) {
              const original = element.dataset.sliderOriginalTabindex;
              original === '' ? element.removeAttribute('tabindex') : element.setAttribute('tabindex', original);
              delete element.dataset.sliderOriginalTabindex;
            } else if (element.getAttribute('tabindex') === '-1') {
              element.removeAttribute('tabindex');
            }
          } else {
            if (element.dataset.sliderOriginalTabindex === undefined) {
              element.dataset.sliderOriginalTabindex = element.getAttribute('tabindex') || '';
            }
            element.setAttribute('tabindex', '-1');
          }
        });
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === nextIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });

      this.currentIndex = nextIndex;
      this.updateControls();

      if (this.status && userInitiated) {
        this.status.textContent = `Banner ${nextIndex + 1} of ${this.slides.length}`;
      }

      if (userInitiated) this.restartAutoplay();
    }

    previous(userInitiated = false) {
      this.show(this.currentIndex - 1, userInitiated);
    }

    next(userInitiated = false) {
      this.show(this.currentIndex + 1, userInitiated);
    }

    updateControls() {
      if (this.loop) return;
      if (this.previousButton) this.previousButton.disabled = this.currentIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.currentIndex === this.slides.length - 1;
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.reducedMotion || this.slides.length < 2 || this.timer) return;
      if (!this.loop && this.currentIndex === this.slides.length - 1) return;
      this.timer = window.setInterval(() => this.next(false), this.interval);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('dawn-banner-slider', DawnBannerSlider);
}
